/**
 * What a text track SHOWS over time, and how far two displays differ (W1-10b core-commands-motion; DESIGN.md §6.4,
 * §6.8; rpe-format.md F§9.3).
 *
 * Phira's string tween turns a text segment into a step function of time: a type-on / erase / extend / counter
 * segment changes the shown text at the moments its rounded character count (or formatted number) changes. Two
 * curves over the same time range are compared through their runs of constant text:
 *  - `textRuns` samples a display densely (TEXT_RUN_SAMPLES uniform samples by default) and brackets every change
 *    between two samples that differ; the brackets are then halved breadth first (each open bracket once per
 *    round) down to TEXT_RUN_EPS seconds, so each character-change time is known to a microsecond. A text shown
 *    for less than one sample interval between two samples that agree is not seen. The halving spends at most the
 *    evaluations of its `EvalBudget` (TEXT_RUN_MAX_EVALS per call by default; a command measuring many segments
 *    shares one budget between them, `textRunsShared`): when the budget cannot halve every open bracket once more,
 *    a display that changes very often (a counter over thousands of numbers) keeps all its changes located to the
 *    same precision, the bracket width (`resolution`), each placed at the end of its bracket;
 *  - `textShift` is the largest distance in time between the two displays: for every moment one of them shows a
 *    text, how far away the other one shows that same text (where the other never shows it, how far away the other
 *    changes its text: the text collapsed into that change), plus the resolution of both measurements, so a
 *    display whose changes could not all be located exactly gives an upper bound. 0 when both show the same text
 *    at all times; for a type-on split it is how much earlier or later a character appears.
 */

/** A stretch of constant displayed text, from chart second `from` until the next run starts (or the range ends). */
export interface TextRun {
  readonly from: number;
  readonly text: string;
}

/** Uniform samples of a display over its range (both ends included). */
export const TEXT_RUN_SAMPLES = 1024;
/** Precision (seconds) to which a change of the displayed text is located. */
export const TEXT_RUN_EPS = 1e-6;
/** Evaluations one `textRuns` call spends on locating changes when no budget is passed. */
export const TEXT_RUN_MAX_EVALS = 20000;
/**
 * Largest shift (seconds) of a character-change time still counted as the same display: 0.1 ms, far below one
 * frame and above the f32 rounding of Phira's progress (used where two displays are equal on paper, e.g. a
 * reversed type-on segment, and for the toast: a shift up to this is reported as no shift).
 */
export const TEXT_EXACT_SHIFT_SEC = 1e-4;

/** Evaluations still available for locating changes (mutated by `textRuns`; shared by several measurements). */
export interface EvalBudget {
  left: number;
}

/** The runs of constant text a display shows over a range, and how precisely their starts are known. */
export interface TextRuns {
  /** In time order; at least one. */
  readonly runs: readonly TextRun[];
  /**
   * Largest width (seconds) of a bracket that still holds a change when the budget ran out (each such change is
   * placed at its bracket's end); 0 when every change was located to TEXT_RUN_EPS.
   */
  readonly resolution: number;
}

/** A change of the shown text inside (l, r], from `tl` to `tr`. */
interface Bracket {
  readonly l: number;
  readonly tl: string;
  readonly r: number;
  readonly tr: string;
}

/**
 * Runs of the text `at(sec)` shows over [t0, t1] (see the file header; `samples` uniform samples before the
 * halving, which spends at most `budget.left` evaluations and leaves the rest in it).
 */
export function textRuns(
  at: (sec: number) => string,
  t0: number,
  t1: number,
  samples: number = TEXT_RUN_SAMPLES,
  budget: EvalBudget = { left: TEXT_RUN_MAX_EVALS },
): TextRuns {
  const runs: TextRun[] = [{ from: t0, text: at(t0) }];
  if (!(t1 > t0)) return { runs, resolution: 0 };
  const eps = Math.max(TEXT_RUN_EPS, (t1 - t0) * 2 ** -40);
  let brackets: Bracket[] = [];
  let prevSec = t0;
  let prevText = (runs[0] as TextRun).text;
  const n = Math.max(1, Math.floor(samples));
  for (let j = 1; j <= n; j++) {
    const sec = j === n ? t1 : t0 + ((t1 - t0) * j) / n;
    const text = at(sec);
    if (text !== prevText) brackets.push({ l: prevSec, tl: prevText, r: sec, tr: text });
    prevSec = sec;
    prevText = text;
  }
  // Breadth first: every open bracket is halved once per round, so when the budget runs out all changes are known
  // to the same precision (a depth-first search would locate the first changes exactly and leave the rest coarse).
  let resolution = 0;
  for (;;) {
    let open = 0;
    let width = 0;
    for (const b of brackets) {
      if (b.r - b.l > eps) {
        open++;
        width = Math.max(width, b.r - b.l);
      }
    }
    if (open === 0) break;
    if (open > budget.left) {
      resolution = width;
      break;
    }
    budget.left -= open;
    const next: Bracket[] = [];
    for (const b of brackets) {
      if (b.r - b.l <= eps) {
        next.push(b);
        continue;
      }
      const m = b.l + (b.r - b.l) / 2;
      const tm = at(m);
      if (tm !== b.tl) next.push({ l: b.l, tl: b.tl, r: m, tr: tm });
      if (tm !== b.tr) next.push({ l: m, tl: tm, r: b.r, tr: b.tr });
    }
    brackets = next;
  }
  for (const b of brackets) {
    const last = runs[runs.length - 1] as TextRun;
    if (last.text !== b.tr) runs.push({ from: b.r, text: b.tr });
  }
  return { runs, resolution };
}

/**
 * `textRuns` for one of `parts` measurements (this one included) that share `pool`: it may spend an equal share
 * of what is left, and what it does not spend stays in the pool for the measurements after it.
 */
export function textRunsShared(
  pool: EvalBudget,
  parts: number,
  at: (sec: number) => string,
  t0: number,
  t1: number,
  samples: number = TEXT_RUN_SAMPLES,
): TextRuns {
  const share: EvalBudget = { left: Math.floor(Math.max(0, pool.left) / Math.max(1, parts)) };
  const given = share.left;
  const out = textRuns(at, t0, t1, samples, share);
  pool.left -= given - share.left;
  return out;
}

/** A closed time interval (a single moment when a === b). */
interface Span {
  readonly a: number;
  readonly b: number;
}

/** Distance from t to the nearest of `set` (sorted, disjoint spans); Infinity for an empty set. */
function distTo(set: readonly Span[], t: number): number {
  let lo = 0;
  let hi = set.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((set[mid] as Span).a <= t) lo = mid + 1;
    else hi = mid;
  }
  let d = Number.POSITIVE_INFINITY;
  const before = set[lo - 1];
  if (before !== undefined) d = Math.min(d, t <= before.b ? 0 : t - before.b);
  const after = set[lo];
  if (after !== undefined) d = Math.min(d, after.a - t);
  return d;
}

/** Largest distance to `set` over [p, q]: at an end, or in the middle of a gap of the set inside (p, q). */
function farthest(set: readonly Span[], p: number, q: number): number {
  let m = Math.max(distTo(set, p), distTo(set, q));
  let lo = 0;
  let hi = set.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((set[mid] as Span).b < p) lo = mid + 1;
    else hi = mid;
  }
  for (let i = Math.max(lo - 1, 0); i + 1 < set.length; i++) {
    const cur = set[i] as Span;
    if (cur.b > q) break;
    const mid = (cur.b + (set[i + 1] as Span).a) / 2;
    if (mid > p && mid < q) m = Math.max(m, distTo(set, mid));
  }
  return m;
}

/** For each run of `x`: how far away `y` shows the same text (or changes, when it never shows it). */
function oneWay(x: readonly TextRun[], y: readonly TextRun[], t1: number): number {
  const byText = new Map<string, Span[]>();
  y.forEach((r, k) => {
    const end = y[k + 1]?.from ?? t1;
    let list = byText.get(r.text);
    if (list === undefined) {
      list = [];
      byText.set(r.text, list);
    }
    list.push({ a: r.from, b: Math.max(r.from, end) });
  });
  const t0 = (y[0] as TextRun).from;
  const changes: Span[] = [{ a: t0, b: t0 }, ...y.slice(1).map((r) => ({ a: r.from, b: r.from })), { a: t1, b: t1 }];
  let m = 0;
  x.forEach((r, k) => {
    const end = x[k + 1]?.from ?? t1;
    m = Math.max(m, farthest(byText.get(r.text) ?? changes, r.from, Math.max(r.from, end)));
  });
  return m;
}

/**
 * Largest distance in time (seconds) between two displays of the same range [.., t1] (see the file header), plus
 * the resolution of both measurements.
 */
export function textShift(x: TextRuns, y: TextRuns, t1: number): number {
  if (x.runs.length === 0 || y.runs.length === 0) return 0;
  return Math.max(oneWay(x.runs, y.runs, t1), oneWay(y.runs, x.runs, t1)) + x.resolution + y.resolution;
}

/** True when a display shows one text over its whole range. */
export function constantText(display: TextRuns): boolean {
  return display.runs.length === 1;
}

/**
 * Whole milliseconds shown for a shift: 0 up to TEXT_EXACT_SHIFT_SEC (the characters appear at the same moments),
 * otherwise rounded up (at least 1).
 */
export function shiftMs(sec: number): number {
  if (!(sec > TEXT_EXACT_SHIFT_SEC)) return 0;
  return Math.max(1, Math.ceil(sec * 1000 - 1e-9));
}
